import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import BookGrid from '../components/BookGrid';
import EnhancedAddBookModal from '../components/EnhancedAddBookModal';
import BookService from '../services/BookService';
import YearInReading from '../components/YearInReading';
import RecentlyAddedBooks from '../components/RecentlyAddedBooks';
import RecentlyFinishedBooks from '../components/RecentlyFinishedBooks';
import EnhancedBookClubs from '../components/EnhancedBookClubs';
import TabNavigation from '../components/TabNavigation';
import './Dashboard.css';

function Dashboard() {
  const { userData, currentUser } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBook, setShowAddBook] = useState(false);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    fetchBooks();
    fetchUserProfile();
  }, [currentUser]);

  const fetchUserProfile = async () => {
    if (!currentUser) return;
    
    try {
      const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
      if (profileDoc.exists()) {
        setUserProfile(profileDoc.data());
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchBooks = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      // Use BookService to get books with duplicate removal
      const booksData = await BookService.getUserBooks(currentUser.uid);
      setBooks(booksData);
    } catch (error) {
      console.error('Error fetching books:', error);
      // Fallback to original query if BookService fails
      try {
        const booksQuery = query(
          collection(db, 'books'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(booksQuery);
        const fallbackBooks = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setBooks(fallbackBooks);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredBooks = books.filter(book => {
    if (filter === 'all') return true;
    // iOS app uses 'readingStatus' field with different values
    const status = book.readingStatus || book.status;
    if (filter === 'to-read') return status === 'toRead';
    if (filter === 'reading') return status === 'reading';
    if (filter === 'finished') return status === 'finished';
    return false;
  });

  const getStatsCount = (status) => {
    return books.filter(book => {
      const bookStatus = book.readingStatus || book.status;
      if (status === 'to-read') return bookStatus === 'toRead';
      if (status === 'reading') return bookStatus === 'reading';
      if (status === 'finished') return bookStatus === 'finished';
      return false;
    }).length;
  };

  const tabs = [
    {
      key: 'overview',
      label: 'Overview',
      icon: '📊',
      badge: null
    },
    {
      key: 'library',
      label: 'Library',
      icon: '📚',
      badge: books.length > 0 ? books.length : null
    },
    {
      key: 'clubs',
      label: 'Book Clubs',
      icon: '👥',
      badge: null
    },
    {
      key: 'analytics',
      label: 'Analytics',
      icon: '📈',
      badge: null
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <div className="stats-grid">
              <div className="stat-card card">
                <div className="stat-icon">📚</div>
                <div className="stat-info">
                  <span className="stat-number">{books.length}</span>
                  <span className="stat-label">Total Books</span>
                </div>
              </div>
              <div className="stat-card card">
                <div className="stat-icon">📖</div>
                <div className="stat-info">
                  <span className="stat-number">{getStatsCount('reading')}</span>
                  <span className="stat-label">Currently Reading</span>
                </div>
              </div>
              <div className="stat-card card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <span className="stat-number">{getStatsCount('finished')}</span>
                  <span className="stat-label">Completed</span>
                </div>
              </div>
              <div className="stat-card card">
                <div className="stat-icon">📋</div>
                <div className="stat-info">
                  <span className="stat-number">{getStatsCount('to-read')}</span>
                  <span className="stat-label">To Read</span>
                </div>
              </div>
            </div>
            
            {books.length > 0 && <RecentlyAddedBooks books={books} />}
            {books.length > 0 && <RecentlyFinishedBooks books={books} />}
          </>
        );
      
      case 'library':
        return (
          <div className="bookshelf-section">
            <div className="section-header">
              <h2>Your Books</h2>
              <div className="filter-tabs">
                {[
                  { key: 'all', label: 'All Books' },
                  { key: 'reading', label: 'Reading' },
                  { key: 'to-read', label: 'To Read' },
                  { key: 'finished', label: 'Finished' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    className={`filter-tab ${filter === tab.key ? 'active' : ''}`}
                    onClick={() => setFilter(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredBooks.length === 0 ? (
              <div className="empty-state card">
                <div className="empty-icon">📚</div>
                <h3>
                  {filter === 'all' 
                    ? "Your bookshelf is empty" 
                    : `No books in "${filter}" status`
                  }
                </h3>
                <p>
                  {filter === 'all'
                    ? "Start building your digital library by adding your first book!"
                    : "Add some books or change their status to see them here."
                  }
                </p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAddBook(true)}
                >
                  Add Your First Book
                </button>
              </div>
            ) : (
              <BookGrid books={filteredBooks} onBookUpdate={fetchBooks} />
            )}
          </div>
        );

      case 'clubs':
        return <EnhancedBookClubs />;
        
      case 'analytics':
        return (
          <>
            {books.length > 0 && <YearInReading books={books} />}
            {books.length === 0 && (
              <div className="empty-state card">
                <div className="empty-icon">📈</div>
                <h3>No Analytics Yet</h3>
                <p>Add some books to your library to see your reading analytics!</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAddBook(true)}
                >
                  Add Your First Book
                </button>
              </div>
            )}
          </>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your bookshelf...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <div className="welcome-section">
            <h1>Welcome back, {userProfile?.displayName || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Reader'}!</h1>
            <p>Your digital bookshelf awaits</p>
          </div>
          
          <button 
            className="btn btn-primary add-book-btn"
            onClick={() => {
              console.log('Add book button clicked');
              setShowAddBook(true);
            }}
          >
            <span className="btn-icon">📚</span>
            Add Book
          </button>
        </div>

        <TabNavigation 
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={tabs}
        />

        {renderTabContent()}

        {showAddBook && (
          <EnhancedAddBookModal 
            onClose={() => setShowAddBook(false)}
            onBookAdded={fetchBooks}
          />
        )}
      </div>
    </div>
  );
}

export default Dashboard;